import React from 'react';
import {
  RocketLaunchIcon,
  UserGroupIcon,
  ChatBubbleLeftRightIcon,
  SpeakerWaveIcon,
} from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import ToolCallout from '../../../components/playbooks/ToolCallout';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';

const CultureOffsiteStage2 = () => {
  return (
    <div className="max-w-none text-gray-300">
      <PlaybookSection
        title="Stage 2: How to Facilitate"
        icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />}
        heading="h1"
      >
        {/* TL;DR section */}
        <div className="mb-10">
          <h2 className="text-2xl font-semibold text-white mb-4">TL;DR</h2>
          
          <ExpandableCallout 
            title="What do I need to DO in this stage?" 
            type="info"
            defaultOpen={true}
          >
            <p className="mb-4">You need to do 3 things:</p>
            <div className="space-y-4 mb-4">
              <div className="flex items-start">
                <span className="text-primary-light font-bold mr-3">1</span>
                <p>Set the scene</p>
              </div>
              <div className="flex items-start">
                <span className="text-primary-light font-bold mr-3">2</span>
                <p>Support table groups</p>
              </div>
              <div className="flex items-start">
                <span className="text-primary-light font-bold mr-3">3</span>
                <p>Consolidate the findings</p>
              </div>
            </div>
            <p>Here's what we mean...</p>
          </ExpandableCallout>
        </div>
        
        {/* Tip Callout */}
        <div className="space-y-4 mb-10">
          <ExpandableCallout 
            title="Consider using a throwable microphone" 
            type="tip"
          >
            <p className="text-gray-300">
              It's incredibly helpful to have a throwable microphone if you are facilitating this in person and at scale (70+).
            </p>
            <div className="mt-4">
              <a href="https://www.bax-shop.co.uk/wireless-handheld-microphones/catchbox-mod-throwable-microphone" className="text-primary-light hover:underline" target="_blank" rel="noopener noreferrer">
                Catchbox throwable microphone - a popular brand for this type of microphone that makes group participation easier.
              </a>
            </div>
          </ExpandableCallout>
          
          <ExpandableCallout 
            title="Create a visual session progress board" 
            type="tip"
          >
            <p className="text-gray-300">
              It's handy to create a simple visual Kanban board of the session's progress to help folks see where they are in terms of the agenda. Big sticky notes for each session, move them from "Todo" ⇒ "Doing" ⇒ "Done" as you go through.
            </p>
          </ExpandableCallout>
        </div>

        {/* Set the Scene Section */}
        <PlaybookSection
          title="Set the Scene"
          icon={<SpeakerWaveIcon className="h-7 w-7 text-primary-light" />}
          heading="h2"
          className="mt-8"
        >
          <PlaybookCard>
            <p className="text-gray-300 mb-6">
              So you've got 20, 30, 50, or maybe 100 or 200+ people looking expectantly at you. What are you going to say?
            </p>
            
            <p className="text-gray-300 mb-4">
              Don't worry, we've got you :-)
            </p>
            
            <h3 className="text-lg font-semibold text-white mb-3">Sample talk track:</h3>
            
            <CopyToNotion
              title="Facilitator's Opening Script"
            >
              <div className="p-4 bg-gray-800 rounded-lg">
                <p className="mb-4">
                  Hey folks!
                </p>
                
                <p className="mb-4">
                  How we work, aka our culture is super important, we care about creating an environment where we can all do our best work together. Without aligning on that it's going to be much harder for us to {'{accomplish our company vision/mission}'} and delivering our {'{value to the customer}'}.
                </p>
                
                <p className="mb-4">
                  So with that in mind, we're going to run a session now to do 2 things:
                </p>
                
                <ol className="space-y-2 mb-4 pl-6">
                  <li>
                    <span className="text-primary-light font-bold mr-2">1.</span>
                    Map our current ways of working
                  </li>
                  <li>
                    <span className="text-primary-light font-bold mr-2">2.</span>
                    Look for opportunities to improve (start/stop/continue)
                  </li>
                </ol>
                
                <p className="mb-4">
                  The way we're going to do this is:
                </p>
                
                <ol className="space-y-2 mb-4 pl-6">
                  <li>
                    <span className="text-primary-light font-bold mr-2">1.</span>
                    Work in table groups to map how we currently get stuff done
                  </li>
                  <li>
                    <span className="text-primary-light font-bold mr-2">2.</span>
                    Quick demo per table of findings (I'll pass the mike around!)
                  </li>
                  <li>
                    <span className="text-primary-light font-bold mr-2">3.</span>
                    Mini break ☕
                  </li>
                  <li>
                    <span className="text-primary-light font-bold mr-2">4.</span>
                    Work in table groups to design how we want to work moving forwards
                  </li>
                  <li>
                    <span className="text-primary-light font-bold mr-2">5.</span>
                    Quick demo per table of ideas (I'll pass the mike around again!)
                  </li>
                </ol>
                
                <p className="mb-4">
                  After we've done this, I'll consolidate everything into a single view and share learnings and actions after the offsite. This will form the backbone of our people team's/my focus for the next Q and I'm hoping it will spark some interesting conversation/ideas today!
                </p>
                
                <p className="mb-4">
                  Any questions?
                </p>
                
                <p className="mb-4">
                  {'{5min Q&A}'}
                </p>
                
                <p className="mb-4">
                  Right, let's get into it! 🚀
                </p>
                
                <p className="mb-4">
                  Facilitators, you know who you are, stand up please so your teams know who you are.
                </p>
                
                <p className="mb-4">
                  {'{stand up facilitators}'}
                </p>
                
                <p className="mb-4">
                  Folks, these people know what's going on and are going to keep us moving forwards today so any questions please ask them. I'll be moving between tables so just shout if you get stuck on something.
                </p>
                
                <p className="mb-2">
                  Let's go!
                </p>
              </div>
            </CopyToNotion>
            
            <ExpandableCallout 
              title="Note for distributed teams" 
              type="info"
              className="mt-6"
            >
              <p className="text-gray-300">
                Facilitation is a bit different if you're distributed and depends on your tool choice (e.g. Miro). Essentially you:
              </p>
              <div className="mt-4 space-y-3 pl-6">
                <div className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <p>Split each team into its own breakout room (with everyone working on the same board so it's all in one place)</p>
                </div>
                <div className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <p>Come back together for the demos</p>
                </div>
                <div className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <p>Back to breakout rooms for individual team work</p>
                </div>
                <div className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <p>Back together for the wash-up</p>
                </div>
              </div>
            </ExpandableCallout>
          </PlaybookCard>
        </PlaybookSection>

        {/* Support Table Groups Section */}
        <PlaybookSection
          title="Support the Table Groups"
          icon={<UserGroupIcon className="h-7 w-7 text-primary-light" />}
          heading="h2"
          className="mt-8"
        >
          <PlaybookCard>
            <h3 className="text-lg font-semibold text-white mb-4">Action: Circulate, support and keep time</h3>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded border border-gray-600 mr-3"></div>
                <p>Move amongst the table groups, keeping an eye on the time and their board completion progress. Spend a bit of extra time with any folks who are slow to get started or spending too much time talking vs sticky noting! Try and encourage folks to surface the top 2-3 stickies per area of the board.</p>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded border border-gray-600 mr-3"></div>
                <p>Call-out when each session is half way through and also when it has 10 minutes left to signal to folks it's coming</p>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded border border-gray-600 mr-3"></div>
                <p>When it's time for a demo - throw the microphone to 3+ table groups and give them a few minutes each to summarize their map or design (Depending on the session). If folks have different perspectives, explore that for a couple of minutes, with curiosity and then move forwards.</p>
              </div>
            </div>
            
            <ExpandableCallout
              title="Facilitation Questions"
              type="info"
            >
              <p className="text-gray-300 mb-4">Here are some example questions to help you facilitate this:</p>
              
              <div className="space-y-3">
                <div className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <p>What <strong>behaviors</strong> do we <strong>amplify</strong> as a result of our values?</p>
                </div>
                
                <div className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <p>What <strong>behaviors</strong> do we <strong>outlaw</strong> as a result of our values?</p>
                </div>
                
                <div className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <p>What do <strong>we do and not do?</strong></p>
                </div>
                
                <div className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <p>How do we <strong>communicate</strong> with each other, what are our speeds and tools?</p>
                </div>
                
                <div className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <p>What meetings <strong>do we have synchronously / asynchronously?</strong> <strong>Why</strong>?</p>
                </div>
                
                <div className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <p>How do we have healthy <strong>conflict</strong>? What does unhealthy conflict look like?</p>
                </div>
                
                <div className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <p>How do we ensure <strong>fearlessness</strong> and <strong>equal voice</strong>?</p>
                </div>
                
                <div className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <p>How, when and why do we <strong>celebrate</strong>?</p>
                </div>
                
                <div className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <p>How will we hold ourselves to <strong>account</strong>? What does that look like?</p>
                </div>
                
                <div className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <p>How do we <strong>hire</strong>? Who do we look for?</p>
                </div>
                
                <div className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <p>Why do we <strong>fire</strong> and how do we do it?</p>
                </div>
                
                <div className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <p>How do we <strong>share knowledge</strong>?</p>
                </div>
                
                <div className="flex items-start">
                  <span className="text-primary-light mr-3">—</span>
                  <p>What are our unique <strong>quirks</strong>?</p>
                </div>
              </div>
            </ExpandableCallout>
          </PlaybookCard>
        </PlaybookSection>

        {/* Outcomes Section */}
        <PlaybookSection
          title="Outcomes"
          icon={<ChatBubbleLeftRightIcon className="h-7 w-7 text-primary-light" />}
          heading="h2"
          className="mt-8"
        >
          <PlaybookCard>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded border border-gray-600 mr-3"></div>
                <p>Teams have had a rich conversation and aligned on how work currently gets done ("culture today")</p>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded border border-gray-600 mr-3"></div>
                <p>Teams have ideated together on elements to improve and co-created your "culture tomorrow"</p>
              </div>
            </div>
          </PlaybookCard>
        </PlaybookSection>
      </PlaybookSection>
    </div>
  );
};

export default CultureOffsiteStage2; 